// Google Routes API integration. Used as an alternative to HERE for
// car/light-vehicle routing — Google's restriction database (vias
// interditas, sentidos únicos, ruas privadas) tends to be more current
// in Portugal than HERE's, particularly inside urban areas.
//
// Returns the same shape as `calculateRouteHERE()` so callers can swap
// providers without changing their consumption code:
//   { distance, duration, geometry, legs, roadBreakdown, countryDistances,
//     routeSteps, roadSegments }
//
// NOTA: o Google Routes só tem modo DRIVE (carro) — não modela camião
// (peso/eixo/altura/túneis). Continua a servir os ligeiros; para pesados só é
// usado quando o perfil escolhe explicitamente Google em "Rota (pesados)"
// (truckRoutingProvider), e nesse caso o RouteForm CORRIGE a ETA com a
// velocidade média do perfil, porque o tempo devolvido aqui é de carro.

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const ROUTES_URL = 'https://routes.googleapis.com/directions/v2:computeRoutes';

// Decode a Google encoded-polyline string into an array of [lng, lat]
// pairs. We deliberately ship our own decoder so this util doesn't need
// the Google Maps JS `geometry` library to be loaded — that would
// conflict with @react-google-maps/api's library lock-in across the
// rest of the planner.
function decodePolyline(str, precision = 5) {
  const factor = Math.pow(10, precision);
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < str.length) {
    let result = 0, shift = 0, b;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;

    result = 0; shift = 0;
    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;

    coords.push([lng / factor, lat / factor]);
  }
  return coords;
}

// Light heuristic to classify a road by its name/ref so the planner's
// HUD ("X km de auto-estrada · Y km de IC") still works when Google
// is the provider. Mirrors hereApi.js / orsApi.js logic.
function classifyRoad(name) {
  const refs = (name || '').toUpperCase().split(/[;,/]+/).map(r => r.replace(/\s+/g, '').trim()).filter(Boolean);
  const priority = { motorway: 4, highway: 3, national: 2, local: 1 };
  let best = null;
  for (const r of refs) {
    let type = null;
    if (/^A-?\d+$/.test(r) || /^AP-?\d+$/.test(r) || /^AG-?\d+$/.test(r)) type = 'motorway';
    else if (/^E-?\d+$/.test(r))         type = 'motorway';
    else if (/^(IP|IC)-?\d+$/.test(r))   type = 'highway';
    else if (/^(EN|EM|ER|CM|M|N)-?\d+/.test(r)) type = 'national';
    if (type && priority[type] > (priority[best] || 0)) best = type;
  }
  if (best) return best;
  const n = (name || '').toUpperCase();
  if (/AUTO.?ESTRADA|AUTOPISTA|AUTOV[IÍ]A/.test(n)) return 'motorway';
  if (/ITINER[AÁ]RIO|VARIANTE/.test(n))             return 'highway';
  if (/ESTRADA (NACIONAL|MUNICIPAL|REGIONAL)/.test(n)) return 'national';
  return 'local';
}

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/**
 * Compute a route via Google Routes API v2.
 * @param {[number,number]} origin       [lng, lat]
 * @param {[number,number]} destination  [lng, lat]
 * @param {Array<[number,number]>} waypoints
 * @param {Object} options  { vehicleType, avoidTolls, avoidFerries, avoidMotorways, departureTime }
 * @returns {Promise<{ route, alternatives, warning }>}
 */
// ISO-2 → ISO-3 mapping for tenants we operate in. Used to label
// `countryDistances` consistently with HERE (which uses ISO-3 codes).
const REGION_2_TO_3 = {
  PT: 'PRT', ES: 'ESP', FR: 'FRA', DE: 'DEU', IT: 'ITA',
  AO: 'AGO', NA: 'NAM', ZA: 'ZAF', MZ: 'MOZ', CV: 'CPV',
};

export async function calculateRouteGoogle(origin, destination, waypoints = [], options = {}) {
  if (!GOOGLE_KEY || GOOGLE_KEY.includes('PLACEHOLDER')) {
    throw new Error('Token Google Maps não configurado. Adiciona REACT_APP_GOOGLE_MAPS_KEY ao .env');
  }

  // Region defaults to PT unless tenant operates elsewhere (e.g. AO for
  // Petrotec Angola). Biases routing results and labels country buckets.
  const regionCode = (options.regionCode || 'PT').toUpperCase();
  const regionCode3 = REGION_2_TO_3[regionCode] || regionCode;

  // Google Routes só tem DRIVE (carro). Para pesados, a ETA de carro é
  // corrigida a jusante no RouteForm (distância ÷ velocidade média do perfil).
  const travelMode = 'DRIVE';

  // Avoid features map cleanly to routeModifiers.
  const routeModifiers = {};
  if (options.avoidTolls)     routeModifiers.avoidTolls = true;
  if (options.avoidFerries)   routeModifiers.avoidFerries = true;
  if (options.avoidMotorways) routeModifiers.avoidHighways = true;

  const intermediates = (waypoints || []).map(([lng, lat]) => ({
    location: { latLng: { latitude: lat, longitude: lng } },
  }));

  const body = {
    origin:      { location: { latLng: { latitude: origin[1],      longitude: origin[0] } } },
    destination: { location: { latLng: { latitude: destination[1], longitude: destination[0] } } },
    intermediates,
    travelMode,
    routingPreference: 'TRAFFIC_AWARE',
    polylineQuality: 'HIGH_QUALITY',
    polylineEncoding: 'ENCODED_POLYLINE',
    computeAlternativeRoutes: !!options.alternatives,
    routeModifiers: Object.keys(routeModifiers).length ? routeModifiers : undefined,
    languageCode: 'pt-PT',
    regionCode,
    units: 'METRIC',
  };
  if (options.departureTime) {
    body.departureTime = new Date(options.departureTime).toISOString();
  }

  const fieldMask = [
    'routes.duration',
    'routes.distanceMeters',
    'routes.polyline.encodedPolyline',
    'routes.legs.duration',
    'routes.legs.distanceMeters',
    'routes.legs.startLocation',
    'routes.legs.endLocation',
    'routes.legs.steps.distanceMeters',
    'routes.legs.steps.staticDuration',
    'routes.legs.steps.polyline.encodedPolyline',
    'routes.legs.steps.navigationInstruction',
    'routes.legs.steps.localizedValues',
  ].join(',');

  const res = await fetch(ROUTES_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': GOOGLE_KEY,
      'X-Goog-FieldMask': fieldMask,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const msg = errBody?.error?.message || `Google Routes ${res.status}`;
    // The most common 403 is "Routes API has not been enabled" — surface
    // that clearly so the operator can act on it.
    if (res.status === 403) {
      throw new Error(`Google Routes API não está activa. Active em https://console.cloud.google.com/apis/library/routes.googleapis.com — ${msg}`);
    }
    throw new Error(msg);
  }

  const data = await res.json();
  if (!data.routes || !data.routes.length) {
    throw new Error('Nenhuma rota encontrada entre os pontos indicados');
  }

  const [main, ...alts] = data.routes;
  return {
    route: parseGoogleRoute(main, regionCode, regionCode3),
    alternatives: alts.map(a => parseGoogleRoute(a, regionCode, regionCode3)),
    warning: null,
  };
}

// Convert a Google Routes v2 response to the same internal shape that
// callers already consume from `calculateRouteHERE`.
function parseGoogleRoute(r, regionCode = 'PT', regionCode3 = 'PRT') {
  const totalCoords = r.polyline?.encodedPolyline
    ? decodePolyline(r.polyline.encodedPolyline) : [];
  const distance = Number(r.distanceMeters) || 0;
  // Google returns durations as ISO 8601 strings ("1234s") sometimes, or
  // as objects {seconds, nanos}. Normalise.
  const parseDur = (d) => {
    if (!d) return 0;
    if (typeof d === 'string') return parseFloat(d.replace(/[^\d.]/g, '')) || 0;
    return Number(d.seconds) || 0;
  };
  const duration = parseDur(r.duration);

  const legs = (r.legs || []).map((leg) => {
    const legCoords = (leg.steps || []).flatMap((step, i) => {
      const c = step.polyline?.encodedPolyline ? decodePolyline(step.polyline.encodedPolyline) : [];
      // Avoid duplicate vertices at step boundaries.
      return i === 0 ? c : c.slice(1);
    });
    return {
      distance: Number(leg.distanceMeters) || 0,
      duration: parseDur(leg.duration),
      geometry: { type: 'LineString', coordinates: legCoords },
    };
  });

  // Build per-step `routeSteps` (turn-by-turn instructions) e
  // `roadSegments` (alimenta o HUD do breakdown por classe).
  // Google Routes v2 não expõe `names`/`functionalClass` por segmento
  // como o HERE — só dá a `navigationInstruction` localizada. Daí
  // extraímos a melhor approximation: ref (A1, IC2, EN10…) por regex
  // e nome legível removendo as palavras de navegação ("Continue na",
  // "Vire à direita em", "Mantenha-se em") até o ref ou pontuação.
  // Steps consecutivos com a mesma estrada são consolidados numa única
  // entrada (igual ao HERE), para o painel não ficar com 50 linhas.
  const routeSteps = [];
  const roadSegments = [];
  let cursor = 0;
  let lastStep = null;

  // Padrões PT/EN para arrancar com "Continuar/Continue/Vire/Turn/…
  // em/onto/pela/…". O grupo capturador apanha o nome da via.
  const ROAD_PREFIX_RE = /^(?:continu[ae]r?|continue|siga|vire|vira|virar|turn|head|mantenha-se|keep|merge|prossiga|proceed)(?:\s+\w+){0,3}\s+(?:em|na|no|onto|on|via|pela|pelo|para a|para o|towards?|em direção a|em direcção a)\s+/i;
  // Quando não há prefixo (estrada já indicada no início), o nome
  // termina no primeiro ref ou pontuação.
  const NAME_TRIM_RE = /\s*(?:para\s|towards?\s|em direção\s|em direcção\s|\.|,|\(|—|-).*$/i;
  // Limpa caracteres de HTML que a Google às vezes manda no texto.
  const stripHtml = s => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

  for (const leg of (r.legs || [])) {
    for (const step of (leg.steps || [])) {
      const text = stripHtml(step.navigationInstruction?.instructions);
      const distM = Number(step.distanceMeters) || 0;
      const durS = parseDur(step.staticDuration);
      const stepCoords = step.polyline?.encodedPolyline
        ? decodePolyline(step.polyline.encodedPolyline) : [];

      const refMatch = text.match(/\b([A-Z]{1,3}-?\d+)\b/);
      const ref = refMatch ? refMatch[1] : null;

      // Nome legível da via: tira o verbo de navegação + termina no ref
      // ou pontuação. Se sobrar muito pouco, descarta para o painel não
      // ficar com strings sem significado.
      let name = text.replace(ROAD_PREFIX_RE, '').replace(NAME_TRIM_RE, '').trim();
      if (ref) name = name.replace(new RegExp(`\\b${ref}\\b`, 'g'), '').replace(/\s+/g, ' ').trim();
      if (name.length < 3) name = null;

      const label = ref || name || (text ? text.slice(0, 40) : 'Sem nome');
      const roadClass = classifyRoad(ref || name || text);
      // País — só temos o regionCode global da request; aplicado a
      // todos os steps. Sem fallback fino entre PT/ES como o HERE.
      const country = (r.regionCode || regionCode).toUpperCase();

      // Consolida com o step anterior quando partilham o mesmo label
      // (mesma estrada continuada). Igual à lógica do HERE.
      if (lastStep && lastStep.label === label) {
        lastStep.distance += distM;
        lastStep.duration = (lastStep.duration || 0) + durS;
        lastStep.endIndex = cursor + Math.max(0, stepCoords.length - 1);
      } else {
        const entry = {
          instruction: text,
          ref, name, label,
          type: roadClass,
          country,
          distance: distM,
          duration: durS,
          startIndex: cursor,
          endIndex: cursor + Math.max(0, stepCoords.length - 1),
        };
        routeSteps.push(entry);
        lastStep = entry;
      }

      roadSegments.push({
        name: ref || name || text,
        distance: distM,
        roadClass,
      });
      cursor += Math.max(0, stepCoords.length - (routeSteps.length === 1 ? 0 : 1));
    }
  }

  // roadBreakdown — total distance per road class.
  const breakdown = { motorway: 0, highway: 0, national: 0, local: 0 };
  for (const seg of roadSegments) breakdown[seg.roadClass] = (breakdown[seg.roadClass] || 0) + seg.distance;

  // countryDistances — Google Routes v2 doesn't surface country spans
  // in the field mask we use, so we approximate with a single-bucket
  // tally keyed by the tenant's region. Good enough for the HUD; users
  // who need fine-grained multi-country splits should keep HERE.
  const countryDistances = { [regionCode3]: distance };

  return {
    distance,
    duration,
    geometry: { type: 'LineString', coordinates: totalCoords },
    legs,
    roadBreakdown: breakdown,
    countryDistances,
    routeSteps,
    roadSegments,
  };
}
